import { CaretDown, Check, Gear } from "@phosphor-icons/react"
import { Button } from "@tokenterminal/core/ui/button/Button"
import { Avatar } from "@tokenterminal/core/ui/icon/Avatar"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { MenuButton } from "@tokenterminal/core/ui/menu/MenuButton"
import { useLocalStorage } from "hooks/useLocalStorage"
import { atom } from "jotai"
import { useCallback, useMemo, useState } from "react"
import { twMerge } from "tailwind-merge"
import { CopyAsImageModal } from "../CopyAsImage/CopyAsImageModal"
import { ChartConfigSidebar } from "./ChartConfigSidebar"
import { ChartImagePreviewWrapper } from "./ChartImagePreviewWrapper"
import { useSourcesAndDefinitionsTextForChartSeries } from "./hooks/useSourcesAndDefinitionsText"
import {
  dimensionsForAspectRatio,
  maxHeight,
  maxWidth,
  paddingPixelsForPadding,
  presets,
  transitionArgs,
  type ChartImageModalProps,
  type ChartImageState,
} from "./types"

export const CopyChartAsImageModal = ({
  isOpen,
  setIsOpen,
  chartConfig,
  defaultProject,
  defaultShowSources,
  defaultShowFootnote,
  renderContent,
}: ChartImageModalProps) => {
  const [storedInitialPreset, setStoredInitialPreset] = useLocalStorage(
    "initial-copy-chart-image-preset",
    presets.default.id
  )
  const initialPreset = presets[storedInitialPreset]
    ? presets[storedInitialPreset]
    : presets.default
  const defaultStyles: ChartImageState = useMemo(
    () =>
      initialPreset.makePreset({
        defaultProject,
        defaultShowSources,
        defaultShowFootnote,
      }),
    [initialPreset, defaultProject, defaultShowSources, defaultShowFootnote]
  )
  const [state, setState] = useState<ChartImageState>(defaultStyles)
  const [showSettings, setShowSettings] = useState(false)

  const seriesAtom = useMemo(() => atom(chartConfig.configs), [chartConfig])
  const sourcesAndDefinitionsText = useSourcesAndDefinitionsTextForChartSeries({
    serieSettingsAtom: seriesAtom,
  })

  let image: React.ReactNode | undefined
  if (state.image?.type === "project" && state.image?.projectId) {
    image = (
      <Avatar
        src={`https://static1.tokenterminal.com/${state.image.projectLogo}`}
        className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
      />
    )
  } else if (state.image?.type === "custom" && state.image.customUrl) {
    image = (
      <Avatar
        src={state.image.customUrl}
        className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0"
      />
    )
  } else if (state.image?.type !== "none") {
    image = (
      <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0 bg-neutral-500/20" />
    )
  }

  const resetActivePreset = useCallback(() => {
    setState(
      presets[state.presetId || "default"].makePreset({
        defaultProject,
        defaultShowSources,
        defaultShowFootnote,
      })
    )
  }, [defaultProject, defaultShowSources, defaultShowFootnote, state.presetId])

  const isPresetDirty = useMemo(() => {
    if (!state.presetId) return false

    const preset = presets[state.presetId].makePreset({
      defaultProject,
      defaultShowSources,
      defaultShowFootnote,
    })

    return (
      state.theme !== preset.theme ||
      state.showBackground !== preset.showBackground ||
      state.background.type !== preset.background.type ||
      state.background.presetKey !== preset.background.presetKey ||
      state.background.customColor !== preset.background.customColor ||
      state.aspectRatio !== preset.aspectRatio ||
      state.sourcesAndFootnoteDisplay !== preset.sourcesAndFootnoteDisplay ||
      state.showSources !== preset.showSources ||
      state.showFootnote !== preset.showFootnote ||
      state.padding !== preset.padding ||
      state.image.type !== preset.image.type ||
      state.image.projectId !== preset.image.projectId ||
      state.image.projectName !== preset.image.projectName ||
      state.image.projectLogo !== preset.image.projectLogo ||
      state.image.customUrl !== preset.image.customUrl
    )
  }, [defaultProject, defaultShowFootnote, defaultShowSources, state])

  const canvasWidth = showSettings
    ? maxWidth + paddingPixelsForPadding.large * 2
    : dimensionsForAspectRatio[state.aspectRatio].width +
      paddingPixelsForPadding[state.padding] * 2
  const canvasHeight = showSettings
    ? maxHeight + paddingPixelsForPadding.large * 2
    : dimensionsForAspectRatio[state.aspectRatio].height +
      paddingPixelsForPadding[state.padding] * 2

  return (
    <CopyAsImageModal
      isOpen={isOpen}
      setIsOpen={(newIsOpen) => {
        setIsOpen(newIsOpen)
        setShowSettings(false)
      }}
      leftHeadingSlot={
        showSettings ? (
          <Button onPress={() => setShowSettings(false)}>
            Done customizing
          </Button>
        ) : (
          <div className="flex items-center gap-2">
            <MenuButton
              trigger={
                <Button className="!rounded-full" size="small">
                  <span className="text-fg-secondary font-normal">Preset</span>
                  <div className="flex items-center gap-1">
                    {state.presetId !== undefined
                      ? presets[state.presetId!]?.label
                      : "Select preset"}
                  </div>
                  <Icon as={CaretDown} />
                </Button>
              }
              placement="bottom end"
            >
              {Object.entries(presets).map(([key, preset]) => (
                <MenuButton.Item
                  key={preset.id}
                  onAction={() => {
                    setStoredInitialPreset(preset.id)
                    setState(
                      preset.makePreset({
                        defaultProject,
                        defaultShowSources,
                        defaultShowFootnote,
                      })
                    )
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Icon
                      as={Check}
                      className="text-fg-secondary"
                      style={{
                        opacity: preset.id === state.presetId ? 1 : 0,
                      }}
                    />
                    {preset.label}
                  </div>
                </MenuButton.Item>
              ))}
              <MenuButton.Separator />
              <MenuButton.Item onAction={() => setShowSettings(true)}>
                <div className="flex items-center gap-2">
                  <Icon as={Gear} />
                  Customize
                </div>
              </MenuButton.Item>
            </MenuButton>
          </div>
        )
      }
    >
      {({ ref }) => (
        <div
          className={twMerge(
            "w-full h-full relative transition-colors p-4",
            showSettings ? "bg-neutral-100 dark:bg-neutral-900/70 my-4" : "my-0"
          )}
        >
          <div
            className="flex w-full h-full"
            style={{
              transition: `padding ${transitionArgs}`,
            }}
          >
            {/* Canvas container */}
            <div
              className="flex items-center justify-center"
              style={{
                width: `${canvasWidth}px`,
                height: `${canvasHeight}px`,
                transition: `width ${transitionArgs}, height ${transitionArgs}`,
              }}
            >
              <ChartImagePreviewWrapper
                innerRef={ref}
                state={state}
                sourcesAndDefinitionsText={sourcesAndDefinitionsText}
                footnoteText={chartConfig.footnote ?? undefined}
                showSettings={showSettings}
                lazyRender
              >
                {renderContent ? renderContent({ projectLogo: image }) : null}
              </ChartImagePreviewWrapper>
            </div>
            <div
              className="relative"
              style={{
                height: `calc(${canvasHeight}px)`,
                width: showSettings ? "370px" : "0px",
                opacity: showSettings ? 1 : 0,
                transition: `width ${transitionArgs}, opacity ${transitionArgs}, height ${transitionArgs}`,
              }}
            >
              <div
                className="absolute top-0 right-0 h-full"
                style={{
                  pointerEvents: showSettings ? "auto" : "none",
                  transform: showSettings
                    ? "translate(0, 0)"
                    : "translate(480px, -10px)",
                  transition: `opacity ${transitionArgs}, transform ${transitionArgs}`,
                }}
              >
                <ChartConfigSidebar
                  state={state}
                  setState={setState}
                  sourcesAndDefinitionsText={sourcesAndDefinitionsText}
                  footnoteText={chartConfig.footnote ?? undefined}
                  defaultProject={defaultProject}
                  setShowSettings={setShowSettings}
                  selectPreset={(presetId) => {
                    setState(
                      presets[presetId].makePreset({
                        defaultProject,
                        defaultShowSources,
                        defaultShowFootnote,
                      })
                    )
                  }}
                  isPresetDirty={isPresetDirty}
                  resetActivePreset={resetActivePreset}
                  canvasHeight={canvasHeight}
                  setStoredInitialPreset={setStoredInitialPreset}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </CopyAsImageModal>
  )
}
